/* eslint-disable @typescript-eslint/camelcase */
import { inject } from 'inversify-props'
import API from '@core/services/API'
import { Component, Vue } from 'vue-property-decorator'
import AppSectionNavi from '@core/components/AppSectionNavi.vue'
import AppForm from '@core/components/AppForm.vue'
import LoadingScreen from '@core/components/LoadingScreen.vue'
import { FormStructure } from '@master/model/structure/FormStructure'
import { IDateService } from '@master/services/IDateService'
import { BscRecord, ClientPolicy, ClientsReviewed, ClientSurvey, Complaint, DocumentsReview, Review } from './bscClasses'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { isEmpty } from 'lodash/fp'
import AppDialog from '@core/components/AppDialog.vue'
import { getChildSections } from '../userProfile/route/index'

interface User {
  oid: string;
  fullname: string;
  systemrole: string;
  email: string;
}
@Component({
  components: {
    AppSectionNavi,
    AppForm,
    LoadingScreen,
    AppDialog
  }
})
export class BscPolicyReviewMixin extends Vue {
  @inject()
  private $api: API

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  @inject()
  private dateService: IDateService

  private userOid = ''
  private bscOid = ''
  private policyOid = ''
  private infractionOid = ''
  private user: User = {
    oid: '',
    fullname: '',
    systemrole: '',
    email: ''
  };

  private bscRecord = new BscRecord()

  private clientsReviewed = new ClientsReviewed()

  private clientPolicy = new ClientPolicy()

  private documentsReview = new DocumentsReview()

  private clientSurvey = new ClientSurvey()

  private newComplaint = new Complaint()

  private isModalDocuments = false
  private activeDocumentsName = 'first'
  private isModalSurvey = false
  private isModalComplaints = false

  private kpi1Reviews: Review[] = [
    {
      question: "Did the representative document all of a client's personal particulars?(Name, age or date of birth, education qualifications, language proficiency)",
      key: 'understanding_needs_client_particulars'
    },
    {
      question: 'Did the representative document the following client information:',
      colspan: true
    },
    {
      question: "a) Financial objectives (Including the client's protection, savings, investments, or heath needs. Time horizon information should be indicated with the financial objectives)",
      key: 'understanding_needs_financial_objectives'
    },
    {
      question: 'b) Risk tolerance or profile',
      key: 'understanding_needs_risk_tolerance'
    },
    {
      question: 'c) Occupation or employment status',
      key: 'understanding_needs_employment_status'
    },
    {
      question: 'd) Financial situation (Including assets, liabilities, cash flow and annual income)',
      key: 'understanding_needs_financial_situation'
    },
    {
      question: 'e) Source and amount of regular income',
      key: 'understanding_needs_regular_income'
    },
    {
      question: 'f) Financial commitments of the client',
      key: 'understanding_needs_financial_commitment'
    },
    {
      question: 'g) Current investment portfolio, including any life policies or collective investment schemes',
      key: 'understanding_needs_investment_portfolio'
    },
    {
      question: "h) Proportion of amount to be invested to total assets (i.e. whether the amount to be invested is a substantial portion of the client's assets)",
      key: 'understanding_needs_proportion_amount'
    },
    {
      question: 'i) For any recommendation made in respect of any life policy, the number of dependants of the client and the extent and duration of financial support required for each of the dependants where the client has a joint investment application, did the representative conduct the Client Knowledge Assessment for both account owners?',
      key: 'understanding_needs_conduct_cka'
    }
  ]

  private kpi2Reviews: Review[] = [
    {
      question: 'Did the representative document the assumptions used for his recommendation (e.g. return on investment, inflation rate, education costs, or retirement age of the client) and are these assumptions reasonable?',
      key: 'sustainability_assumptions_used'
    },
    {
      question: 'Did the representative conduct a risk profiling for the client?',
      key: 'sustainability_risk_profiling'
    },
    {
      question: 'Did the representative document the basis of recommendation for the investment product recommended?',
      key: 'sustainability_basis_of_recommendation'
    },
    {
      question: 'Did the representative recommend an investment product which: ',
      colspan: true
    },
    {
      question: 'a) meets the financial objectives of the client',
      key: 'sustainability_financial_objectives'
    },
    {
      question: 'b) is affordable to the client',
      key: 'sustainability_is_affordable'
    },
    {
      question: "c) is aligned with the client's investment horizon;",
      key: 'sustainability_aligned_investment_horizon'
    },
    {
      question: "d) matches the client's risk profile;",
      key: 'sustainability_match_risk_profile'
    },
    {
      question: "e) takes into account the client's concentration risk or",
      key: 'sustainability_concentration_risk'
    },
    {
      question: "f) takes into consideration the particular needs of the client (e.g. the number and age of the client's dependants)",
      key: 'sustainability_particular_needs'
    },
    {
      question: 'Is there a file note, call-log or document annotation (whichever is applicable) on the client call-back by the supervisor?',
      key: 'sustainability_client_callback'
    },
    {
      question: 'Depending on whether there are facts and circumstances in a transaction which warrant a closer scrutiny, was the client callback adequately performed by the supervisor and did the supervisor ask the client relevant questions in relation to the transaction(e.g. if the client is an elderly person who has invested his CPF monies, did the supervisor ask if the client was aware on how the returns offered by the investment product compared with the interest rates earned by monies in the different CPF accounts held by the client?)',
      key: 'sustainability_callback_adequately_performed'
    }
  ]

  private kpi3Reviews: Review[] = [
    {
      question: 'Did the client sign the risk disclosure statement accompanying the investment product?',
      key: 'disclosure_client_sign'
    },
    {
      question: 'Did the client acknowledge receipt of the following documents:',
      colspan: true
    },
    {
      question: 'a) Financial Needs Analysis (Fact-find form, the specific recommendations of the representative and the basis for the recommendation)',
      key: 'disclosure_needs_analysis'
    },
    {
      question: 'b) Prospectus, fund factsheet, Product Summary or Product Highlight Sheet of the investment product',
      key: 'disclosure_prospectus'
    },
    {
      question: 'c) Benefit Illustration (for life policies)',
      key: 'disclosure_benefit_illustration'
    },
    {
      question: 'Is there a written acknowledgement from the client, that the following information, has been explained to the client:\n(Note: A financial adviser should refer to the Notice on Information to Clients and Product Information Disclosure [Notice No. FAA N03] on its disclosure requirements)',
      colspan: true
    },
    {
      question: 'a) Nature and objective of the investment product',
      key: 'disclosure_nature_objective'
    },
    {
      question: 'b) Details of the product provider',
      key: 'disclosure_details_provider'
    },
    {
      question: 'c) Party against whom the client may take action to enforce his rights with respect to the investment product purchased by the client',
      key: 'disclosure_party_against'
    },
    {
      question: 'd) Intended client profile of the investment product',
      key: 'disclosure_intended_client_profile'
    },
    {
      question: 'e) Frequency of the reports and source from which the client could reasonably be expected to receive the reports on the investment product',
      key: 'disclosure_frequency_reports'
    },
    {
      question: 'f) Amount of, frequency with which, and period over which payment is to be made by the client in respect of the investment product',
      key: 'disclosure_frequency_period_payment'
    },
    {
      question: 'g) Benefits of the investment product, including the amount and timing for payment of benefits by the product provider and whether the benefits are guaranteed or non-guaranteed. For example:\n- Whether capital or return is guaranteed\n- Where the investment product is a life policy, reasons why any benefit is excluded by the life insurer in respect of the life policy',
      key: 'disclosure_benefits_product'
    },
    {
      question: 'h) Risk factors of the investment product',
      key: 'disclosure_risk_factors'
    },
    {
      question: 'i) Amount, frequency of payment, and nature, of fees and charges to be borne by the client',
      key: 'disclosure_amount_frequency_payment'
    },
    {
      question: 'j) Procedures, charges and restrictions on withdrawal or surrender of, or claim on, the investment product',
      key: 'disclosure_procedures_withdrawal'
    },
    {
      question: 'k) Warnings, exclusions and caveats in relation to the investment product',
      key: 'disclosure_warnings_exclusions'
    },
    {
      question: 'l) Free-look period for life policies or cancellation period for unit trusts (whichever is applicable)',
      key: 'disclosure_freelook_period'
    }
  ]

  private kpi4Reviews: Review[] = [
    {
      question: 'Based on documentation reviewed, are there any indications that a representative has conducted himself unprofessionally or unethically?\nFor example, are there any indications that the documentation has been tampered with (e.g. use of correction-pens on material information and the correction made has not been acknowledged by client)?',
      key: 'ethics_any_indications'
    }
  ]

  private kpi5Reviews: Review[] = [
    {
      question: 'Did the representative ask you questions in order to understand your financial situation, investment objective and risk appetite before recommending an investment product?',
      key: 'comprehensive_understand_financial_situation'
    },
    {
      question: 'Was there any instance during the financial advisory process where you felt that your representative had influenced you to provide an answer or influenced you to modify your original answer to an answer that you are uncomfortable with as it is inaccurate or does not represent your intention?',
      key: 'comprehensive_felt_influenced'
    }
  ]

  private kpi6Reviews: Review[] = [
    {
      question: 'Did you understand the representative\'s explanation as to why an investment product is suitable for you?',
      key: 'suitability_understand_explanation'
    }
  ]

  private kpi7Reviews: Review[] = [
    {
      question: 'Did the representative inform you that you have purchased a .....(Please refer to type of investment product as set out in the first column of the table below), and are you aware that (Please refer to corresponding brief description of the investment product as set out in the second column of the table below)?',
      key: 'disclosures_inform_aware'
    }
  ]

  private kpi8Reviews: Review[] = [
    {
      question: 'Were there any aspects of the sales process that you were dissatisfied with?',
      key: 'ethics_aspect_dissatisfied'
    }
  ]

  private complaints: Complaint[] = []

  async mounted () {
    // take injected UIService and set it to local
    this.uiService = this.sharedVariable.uiService

    this.userOid = this.$router.currentRoute.query.userOid as string
    this.bscOid = this.$router.currentRoute.query.bscOid as string
    this.policyOid = this.$router.currentRoute.query.policyOid as string
    this.infractionOid = this.$router.currentRoute.query.infractionOid as string
    await this.getUser()
    await this.getBscRecord()
    await this.getClientsReviewed()
    await this.getClientPolicy()
  }

  async getUser () {
    if (!this.userOid) {
      this.user.oid = this.currentUser.accountID
      this.user.fullname = this.currentUser.fullname
      this.user.systemrole = this.currentUser.systemrole
      this.user.email = this.currentUser.email
    } else {
      const response = await this.$api.account
        .loginMetaGet({
          _oid: this.userOid
        })
      this.user.oid = response.meta._oid
      this.user.fullname = response.meta.fullname
      this.user.systemrole = response.meta.systemrole
      this.user.email = response.meta.email
    }
  }

  async getBscRecord () {
    const response = await this.$api.bscRecords.getReviewMeta({
      _oid: this.bscOid
    })
    this.bscRecord = response.meta
  }

  get sections () {
    const sectionsList = getChildSections(this.currentUser.systemrole, this.$router) || []
    return sectionsList.map(routes => {
      return {
        ...routes,
        path: `/user/${this.userOid}/${routes.path}`
      }
    }) || []
  }

  get newComplaintStructure (): FormStructure<Complaint> {
    return [
      {
        label: 'Complaint',
        key: 'complaint_title',
        disabled: this.isDisabled
      },
      {
        label: 'Score',
        key: 'complaint_score',
        disabled: this.isDisabled
      },
      {
        label: 'Comments',
        key: 'complaint_comments',
        type: 'textarea',
        classes: ['colspan'],
        disabled: this.isDisabled
      }
    ]
  }

  get bscRecordStructure (): FormStructure<BscRecord> {
    return [
      {
        label: 'Date Completed',
        key: 'date_completed',
        type: 'date',
        disabled: true
      },
      {
        label: 'Representative Name',
        key: 'adviser_name',
        disabled: true
      },
      {
        label: 'ISAU Reviewer',
        key: 'isau_reviewer',
        disabled: true
      },
      {
        label: 'BSC Review Year',
        key: 'review_year',
        disabled: true
      },
      {
        label: 'BSC Review Quarter',
        key: 'review_quarter',
        disabled: true
      }
    ]
  }

  get currentUser () {
    return this.$store.getters.currentUser
  }

  get isDisabled () {
    const userRole = this.currentUser.systemrole
    return !(userRole === 'ops-staff' || userRole === 'bsc-reviewer')
  }

  bscReviewerRestriction (sectionName) {
    return this.currentUser.systemrole === 'bsc-reviewer' && sectionName !== 'User BSC Records'
  }

  async getClientsReviewed () {
    if (!this.infractionOid) {
      await this.uiService.alert.show({
        type: 'error',
        message: 'Invalid Policy Infraction Oid'
      })
      return
    }
    const response = await this.$api.bscRecords.getClientMeta({
      _oid: this.infractionOid
    })
    if (!response.meta) {
      return
    }

    this.clientsReviewed = response.meta
    if (!isEmpty(this.clientsReviewed.documents_review)) this.documentsReview = JSON.parse(response.meta.documents_review)
    if (!isEmpty(this.clientsReviewed.client_survey)) this.clientSurvey = JSON.parse(response.meta.client_survey)
    if (!isEmpty(this.clientsReviewed.complaints)) this.complaints = JSON.parse(response.meta.complaints)
  }

  get clientsReviewedtructure1 (): FormStructure<ClientsReviewed> {
    return [
      {
        label: 'Client Name',
        key: 'client_name',
        disabled: true
      },
      {
        label: 'Sample Rounds',
        key: 'sample_rounds',
        disabled: true
      }
    ]
  }

  get clientsReviewedtructure2 (): FormStructure<ClientsReviewed> {
    return [
      {
        label: 'Cat 1 Infractions',
        key: 'cat_1_infraction_count',
        disabled: this.isDisabled
      },
      {
        label: 'Cat 2 Infractions',
        key: 'cat_2_infraction_count',
        disabled: this.isDisabled
      },
      {
        label: 'Admin Errors',
        key: 'admin_error_count',
        disabled: this.isDisabled
      },
      {
        label: 'Infraction Details',
        key: 'infraction_details',
        type: 'textarea',
        classes: ['colspan'],
        disabled: this.isDisabled
      },
      {
        label: 'Administrative Errors Details',
        key: 'admin_error_details',
        type: 'textarea',
        classes: ['colspan'],
        disabled: this.isDisabled
      }
    ]
  }

  async getClientPolicy () {
    const response = await this.$api.clientPolicy.meta({
      _oid: this.clientsReviewed.policy_oid
    })
    if (!response.meta) {
      return
    }
    this.clientPolicy.policy_name = response.meta.name
    this.clientPolicy.policy_number = response.meta.policy_number
    this.clientPolicy.tagged_kyc = response.meta.tagged_kyc ? 'kyc-pdf/' + response.meta.tagged_kyc : ''
    this.clientPolicy.client_oid = response.meta.client_oid
    this.clientPolicy.policy_url = `/live/policy/${this.clientsReviewed.policy_oid}/detail/nbr`
  }

  get clientPolicyStructure (): FormStructure<ClientPolicy> {
    return [
      {
        label: 'Product Name',
        key: 'policy_name',
        disabled: true
      },
      {
        label: `Plan Reference No <br>&lt;<a href="${this.clientPolicy.policy_url}" target="_blank" style="color: #32CD32;">Page Link</a>&gt;`,
        key: 'policy_number',
        disabled: true
      }
    ]
  }

  get kpi1 () {
    let score = 0
    if (this.documentsReview.understanding_needs_client_particulars) {
      score++
    }
    if (this.documentsReview.understanding_needs_financial_objectives) {
      score++
    }
    if (this.documentsReview.understanding_needs_risk_tolerance) {
      score++
    }
    if (this.documentsReview.understanding_needs_employment_status) {
      score++
    }
    if (this.documentsReview.understanding_needs_financial_situation) {
      score++
    }
    if (this.documentsReview.understanding_needs_regular_income) {
      score++
    }
    if (this.documentsReview.understanding_needs_financial_commitment) {
      score++
    }
    if (this.documentsReview.understanding_needs_investment_portfolio) {
      score++
    }
    if (this.documentsReview.understanding_needs_proportion_amount) {
      score++
    }
    if (this.documentsReview.understanding_needs_conduct_cka) {
      score++
    }
    return ((score / 10) * 100).toFixed(0)
  }

  get kpi2 () {
    let score = 0
    if (this.documentsReview.sustainability_assumptions_used) {
      score++
    }
    if (this.documentsReview.sustainability_risk_profiling) {
      score++
    }
    if (this.documentsReview.sustainability_basis_of_recommendation) {
      score++
    }
    if (this.documentsReview.sustainability_financial_objectives) {
      score++
    }
    if (this.documentsReview.sustainability_is_affordable) {
      score++
    }
    if (this.documentsReview.sustainability_aligned_investment_horizon) {
      score++
    }
    if (this.documentsReview.sustainability_match_risk_profile) {
      score++
    }
    if (this.documentsReview.sustainability_concentration_risk) {
      score++
    }
    if (this.documentsReview.sustainability_particular_needs) {
      score++
    }
    if (this.documentsReview.sustainability_client_callback) {
      score++
    }
    if (this.documentsReview.sustainability_callback_adequately_performed) {
      score++
    }
    return ((score / 11) * 100).toFixed(0)
  }

  get kpi3 () {
    let score = 0
    if (this.documentsReview.disclosure_client_sign) {
      score++
    }
    if (this.documentsReview.disclosure_needs_analysis) {
      score++
    }
    if (this.documentsReview.disclosure_prospectus) {
      score++
    }
    if (this.documentsReview.disclosure_benefit_illustration) {
      score++
    }
    if (this.documentsReview.disclosure_nature_objective) {
      score++
    }
    if (this.documentsReview.disclosure_details_provider) {
      score++
    }
    if (this.documentsReview.disclosure_party_against) {
      score++
    }
    if (this.documentsReview.disclosure_intended_client_profile) {
      score++
    }
    if (this.documentsReview.disclosure_frequency_reports) {
      score++
    }
    if (this.documentsReview.disclosure_frequency_period_payment) {
      score++
    }
    if (this.documentsReview.disclosure_benefits_product) {
      score++
    }
    if (this.documentsReview.disclosure_risk_factors) {
      score++
    }
    if (this.documentsReview.disclosure_amount_frequency_payment) {
      score++
    }
    if (this.documentsReview.disclosure_procedures_withdrawal) {
      score++
    }
    if (this.documentsReview.disclosure_warnings_exclusions) {
      score++
    }
    if (this.documentsReview.disclosure_freelook_period) {
      score++
    }
    return ((score / 16) * 100).toFixed(0)
  }

  get kpi4 () {
    let score = 0
    if (this.documentsReview.ethics_any_indications) {
      score++
    }
    return ((score / 1) * 100).toFixed(0)
  }

  get kpi5 () {
    let score = 0
    if (this.clientSurvey.comprehensive_understand_financial_situation) {
      score++
    }
    if (this.clientSurvey.comprehensive_felt_influenced) {
      score++
    }
    return ((score / 2) * 100).toFixed(0)
  }

  get kpi6 () {
    let score = 0
    if (this.clientSurvey.suitability_understand_explanation) {
      score++
    }
    return ((score / 1) * 100).toFixed(0)
  }

  get kpi7 () {
    let score = 0
    if (this.clientSurvey.disclosures_inform_aware) {
      score++
    }
    return ((score / 1) * 100).toFixed(0)
  }

  get kpi8 () {
    let score = 0
    if (this.clientSurvey.ethics_aspect_dissatisfied) {
      score++
    }
    return ((score / 1) * 100).toFixed(0)
  }

  async onSubmit () {
    const data: any = Object.assign({}, this.clientsReviewed)
    data.documents_review = JSON.stringify(this.documentsReview)
    data.client_survey = JSON.stringify(this.clientSurvey)
    data.complaints = JSON.stringify(this.complaints)
    if (data.cat_1_infraction_count?.toString().length === 0) {
      data.cat_1_infraction_count = null
    }
    if (data.cat_2_infraction_count?.toString().length === 0) {
      data.cat_2_infraction_count = null
    }
    if (data.admin_error_count?.toString().length === 0) {
      data.admin_error_count = null
    }
    await this.$api.bscRecords
      .saveClientMeta({
        _oid: this.infractionOid,
        meta: JSON.stringify(data)
      })

    await this.$api.bscRecords.updateInfractionTotal({
      bsc_quarter_oid: this.bscOid
    })
    await this.uiService.alert.show({
      type: 'success',
      message: 'Save successful'
    })
  }

  async saveComplaint () {
    this.complaints.push(this.newComplaint)
    this.isModalComplaints = false
    this.newComplaint = new Complaint()
  }
}
